import { type ReactNode, useState, useEffect } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useYetki } from '@/hooks/useYetki'
import { BildirimZili } from '@/components/bildirim/BildirimZili'
import {
  LogOut, ShieldCheck, Home, Users, Calendar, Coffee,
  Award, ChevronLeft, Menu, X, ArrowLeftRight, CreditCard, Coins,
  ClipboardList, UserCircle, Wallet,
} from 'lucide-react'
import { YETKILER, type YetkiId } from '@/lib/yetkiler'
import clsx from 'clsx'

interface Props {
  children?: ReactNode
}

interface MenuItem {
  to: string
  icon: typeof Home
  label: string
  /** Bu menüyü görmek için sahip olması gereken yetkiler — herhangi biri yeterli */
  gerekliYetkiler: YetkiId[] | null  // null = her zaman görünür
}

const MENU: MenuItem[] = [
  { to: '/yonetici', icon: Home, label: 'Genel Bakış', gerekliYetkiler: null },
  { to: '/yonetici/personel', icon: Users, label: 'Personel', gerekliYetkiler: [YETKILER.PERSONEL_LISTELE] },
  { to: '/yonetici/vardiyalar', icon: Calendar, label: 'Vardiyalar', gerekliYetkiler: [YETKILER.VARDIYA_GOR, YETKILER.VARDIYA_PLANLA] },
  { to: '/yonetici/gorevler', icon: ClipboardList, label: 'Görevler', gerekliYetkiler: [YETKILER.GOREV_GOR, YETKILER.GOREV_ATA] },
  { to: '/yonetici/giris-cikis', icon: ArrowLeftRight, label: 'Giriş / Çıkış', gerekliYetkiler: null },
  { to: '/yonetici/molalar', icon: Coffee, label: 'Molalar', gerekliYetkiler: [YETKILER.MOLA_GECMIS_GOR, YETKILER.MOLA_ONAYLA] },
  { to: '/yonetici/avanslar', icon: CreditCard, label: 'Avanslar', gerekliYetkiler: [YETKILER.AVANS_LISTE_GOR] },
  { to: '/yonetici/maaslar', icon: Wallet, label: 'Maaşlar', gerekliYetkiler: [YETKILER.MAAS_GORUNTULE] },
  { to: '/yonetici/puantaj', icon: Award, label: 'Puantaj', gerekliYetkiler: [YETKILER.PUANTAJ_EKLE, YETKILER.PUANTAJ_TUM_GOR] },
  { to: '/yonetici/maliyet', icon: Coins, label: 'Maliyet', gerekliYetkiler: [YETKILER.RAPOR_MALIYET] },
]

export function YoneticiLayout({ children }: Props) {
  const { kullanici, cikisYap } = useAuth()
  const [sidebarAcik, setSidebarAcik] = useState(true)
  const [drawerAcik, setDrawerAcik] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setDrawerAcik(false)
  }, [location.pathname])

  if (!kullanici) return null

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside
        className={clsx(
          'hidden md:flex flex-col bg-white border-r border-gray-200 transition-all duration-200',
          sidebarAcik ? 'w-64' : 'w-16'
        )}
      >
        <div className="px-4 py-5 border-b border-gray-200 flex items-center justify-between">
          {sidebarAcik && (
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-brand-600" />
              <span className="font-bold text-gray-900">Shift Beat</span>
            </div>
          )}
          <button
            onClick={() => setSidebarAcik(!sidebarAcik)}
            className="p-1 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className={clsx(
              'w-5 h-5 text-gray-500 transition-transform',
              !sidebarAcik && 'rotate-180'
            )} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {MENU.map((item) => (
            <MenuLink
              key={item.to}
              item={item}
              sidebarAcik={sidebarAcik}
            />
          ))}

          {/* Yönetici aynı zamanda personeldir — kendi giriş-çıkış ekranına geçiş */}
          <NavLink
            to="/personel"
            className="flex items-center gap-3 px-4 py-3 mx-2 mt-2 rounded-xl border border-dashed border-brand-300 text-brand-700 hover:bg-brand-50 transition-colors"
          >
            <UserCircle className="w-5 h-5 flex-shrink-0" />
            {sidebarAcik && <span className="text-sm font-medium">Personel Ekranım</span>}
          </NavLink>
        </nav>

        <div className="border-t border-gray-200 p-3">
          {sidebarAcik ? (
            <div className="space-y-2">
              <div className="px-2 py-1 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">YÖNETİCİ</p>
                  <p className="font-semibold text-gray-900 text-sm truncate">
                    {kullanici.isim}
                  </p>
                </div>
                <BildirimZili kullaniciId={kullanici.id} />
              </div>
              <button
                onClick={cikisYap}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Çıkış</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <BildirimZili kullaniciId={kullanici.id} />
              <button onClick={cikisYap} className="p-2 hover:bg-gray-50 rounded-lg w-full flex justify-center" title="Çıkış">
                <LogOut className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-20 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-brand-600" />
          <span className="font-bold text-gray-900 text-sm">Shift Beat</span>
        </div>
        <div className="flex items-center gap-1">
          <BildirimZili kullaniciId={kullanici.id} />
          <button onClick={() => setDrawerAcik(true)} className="p-2 hover:bg-gray-50 rounded-lg">
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {drawerAcik && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setDrawerAcik(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl p-4 max-h-[85vh] overflow-y-auto animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-brand-600" />
                <h3 className="font-bold text-gray-900">Tüm Menü</h3>
              </div>
              <button
                onClick={() => setDrawerAcik(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {MENU.map((item) => (
                <MenuGridLink key={item.to} item={item} />
              ))}
              <NavLink
                to="/personel"
                className="flex flex-col items-center gap-2 p-3 rounded-xl border border-dashed border-brand-300 bg-white text-brand-700 hover:bg-brand-50 transition-colors"
              >
                <UserCircle className="w-6 h-6" />
                <span className="text-xs font-semibold text-center">Personel Ekranım</span>
              </NavLink>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-gray-500 uppercase">YÖNETİCİ</p>
                <p className="font-semibold text-gray-900 text-sm">{kullanici.isim}</p>
              </div>
              <button
                onClick={cikisYap}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
              >
                <LogOut className="w-4 h-4" />
                <span>Çıkış</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-y-auto pt-14 md:pt-0 pb-6">
        <div className="max-w-6xl mx-auto px-4 py-6 md:px-8">
          {children ?? <Outlet />}
        </div>
      </main>
    </div>
  )
}

function MenuLink({ item, sidebarAcik }: { item: MenuItem; sidebarAcik: boolean }) {
  const yetkili = useYetki(item.gerekliYetkiler ?? [])
  const goster = item.gerekliYetkiler === null || yetkili
  if (!goster) return null
  return (
    <NavLink
      to={item.to}
      end={item.to === '/yonetici'}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-4 py-3 mx-2 rounded-xl transition-colors',
          isActive
            ? 'bg-brand-50 text-brand-700 font-semibold'
            : 'text-gray-600 hover:bg-gray-50',
        )
      }
    >
      <item.icon className="w-5 h-5 flex-shrink-0" />
      {sidebarAcik && <span className="text-sm">{item.label}</span>}
    </NavLink>
  )
}

function MenuGridLink({ item }: { item: MenuItem }) {
  const yetkili = useYetki(item.gerekliYetkiler ?? [])
  const goster = item.gerekliYetkiler === null || yetkili
  if (!goster) return null
  return (
    <NavLink
      to={item.to}
      end={item.to === '/yonetici'}
      className={({ isActive }) =>
        clsx(
          'flex flex-col items-center gap-2 p-3 rounded-xl border transition-colors',
          isActive
            ? 'bg-brand-50 border-brand-200 text-brand-700'
            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
        )
      }
    >
      <item.icon className="w-6 h-6" />
      <span className="text-xs font-semibold text-center">{item.label}</span>
    </NavLink>
  )
}
